/*
 * Description:
 * ---------------------
 * Alerts: sign up for them, see whether anything is wrong right now, and see
 * who else gets told (server/pkg/alerts).
 *
 * notabox-server judges every watched site from the heartbeats and telemetry
 * it already receives, and emails each list when a problem starts and when it
 * ends. Anyone signed in can put an address on their customer's list and gets
 * a confirmation at once; they see and can take off the addresses they added.
 * A list's admins see everyone on it, take anyone off, add chat webhooks and
 * send a test. Each site's lines and the recent history sit behind
 * disclosures. Wording lives in alertWords.js; the alerts' own sentences come
 * from the server, so the screen shows what was sent.
 *
 * ! file components/AlertsPanel.js
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiBell, FiCheckCircle, FiHash, FiMail, FiSend, FiX } from "react-icons/fi";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useSession } from "./PortalGate";
import * as W from "./alertWords";
import "./AlertsPanel.css";

const POLL_MS = 15000;
// A sign-up waits for its confirmation to be sent, which a slow mail relay
// can take most of twenty seconds to refuse.
const SEND_MS = 30000;

export default function AlertsPanel({ serverApiUrl }) {
  const session = useSession();
  const [view, setView] = useState(null);
  const [routes, setRoutes] = useState(null);
  const [error, setError] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const get = useCallback(
    async (path) => {
      const res = await fetchWithTimeout(serverApiUrl(path), { credentials: "same-origin" }).catch(() => null);
      if (!res) throw new Error("Could not reach the Weavian server.");
      if (res.status === 404) throw new Error(W.NOT_ON_SERVER);
      if (!res.ok) throw new Error(`The Weavian server answered ${res.status}.`);
      return res.json();
    },
    [serverApiUrl],
  );

  const loadAlerts = useCallback(async () => {
    try {
      const body = await get("/alerts");
      if (mounted.current) {
        setView(body);
        setError("");
      }
    } catch (err) {
      if (mounted.current) setError(err.message);
    }
  }, [get]);

  const loadRoutes = useCallback(async () => {
    try {
      const body = await get("/alerts/routes");
      if (mounted.current) setRoutes(body);
    } catch (err) {
      if (mounted.current) setError(err.message);
    }
  }, [get]);

  useVisibleInterval(loadAlerts, POLL_MS);
  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  // send makes one change. A refusal carries the server's own reason, which
  // names the rule that was broken; a success carries the lists as they now
  // stand, so the screen never shows a list the server no longer holds.
  const send = useCallback(
    async (path, method, body, timeoutMs = 10000) => {
      const res = await fetchWithTimeout(serverApiUrl(path), {
        method,
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }, timeoutMs).catch(() => null);
      if (!res) return { ok: false, error: "Could not reach the Weavian server." };
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `The Weavian server refused this (${res.status}).` };
      if (data.lists && mounted.current) setRoutes(data.lists);
      return { ok: true, data };
    },
    [serverApiUrl],
  );

  if (!view) {
    return (
      <div className="panel-card al-panel">
        <p className="al-loading">{error || "Reading the watched sites…"}</p>
      </div>
    );
  }

  const domainNames = Object.fromEntries((routes?.lists || []).map((l) => [l.id, l.name]));
  return (
    <div className="al-stack">
      <SignUp routes={routes} send={send} isSuperAdmin={session.isSuperAdmin} />
      <Status view={view} error={error} domainNames={domainNames} />
      <Lists routes={routes} send={send} reload={loadRoutes} />
    </div>
  );
}

/* The one thing most visitors came to do: put their address on a list. */
function SignUp({ routes, send, isSuperAdmin }) {
  const [email, setEmail] = useState("");
  const [route, setRoute] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const lists = routes?.lists || [];
  const chosen = lists.find((l) => l.id === route) || lists[0];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const r = await send("/alerts/recipients", "POST", { route: chosen.id, to: email }, SEND_MS);
    setBusy(false);
    if (!r.ok) {
      setMsg({ ok: false, text: r.error });
      return;
    }
    setMsg({ ok: true, text: W.signedUp(r.data, r.data.lists?.email) });
    setEmail("");
  };

  return (
    <section className="panel-card al-panel al-signup" aria-labelledby="al-signup-title">
      <div className="al-signup-head">
        <span className="al-signup-icon" aria-hidden="true"><FiBell /></span>
        <div>
          <h2 id="al-signup-title">{W.SIGNUP_TITLE}</h2>
          <p>{W.signUpDetail(lists)}</p>
        </div>
      </div>
      {!routes && <p className="al-loading">Reading the lists…</p>}
      {routes && !lists.length && <p className="al-note">{W.NO_LISTS}</p>}
      {chosen && (
        <form className="al-signup-form" onSubmit={submit}>
          <label className="al-email">
            <FiMail aria-hidden="true" />
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com" autoComplete="email" aria-label="Email address"
            />
          </label>
          {lists.length > 1 && (
            <select className="al-select" value={chosen.id} onChange={(e) => setRoute(e.target.value)} aria-label="Which alerts">
              {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          <button type="submit" className="al-btn al-primary" disabled={busy}>
            {busy ? "Signing up…" : "Sign up"}
          </button>
        </form>
      )}
      {chosen && !routes.email && <p className="al-note">{W.emailOff(isSuperAdmin)}</p>}
      {msg && <p className={msg.ok ? "al-notice" : "al-error"} role={msg.ok ? "status" : "alert"}>{msg.text}</p>}
    </section>
  );
}

/* Is anything wrong, with each site's lines and the history behind it. */
function Status({ view, error, domainNames }) {
  const head = W.headline(view);
  const now = Date.now();
  return (
    <div className="panel-card al-panel">
      <div className={`al-head al-${head.tone}`}>
        {head.tone === "good" ? <FiCheckCircle aria-hidden="true" />
          : head.tone === "bad" ? <FiAlertTriangle aria-hidden="true" />
            : <FiBell aria-hidden="true" />}
        <div>
          <h2>{head.title}</h2>
          <p>{head.detail}</p>
        </div>
      </div>
      {error && <p className="al-note">{error}</p>}
      {view.active.length > 0 && (
        <ul className="al-alerts">
          {view.active.map((a) => <AlertItem key={a.key} alert={a} />)}
        </ul>
      )}
      <Sites sites={view.sites} domainNames={domainNames} now={now} />
      <Recent recent={view.recent} />
    </div>
  );
}

function AlertItem({ alert }) {
  return (
    <li className="al-alert">
      <strong>{alert.title}</strong>
      <p>{alert.detail}</p>
    </li>
  );
}

/* Every watched site and the state of each of its lines, the evidence behind
   the headline. Closed by default: the headline is the answer. */
function Sites({ sites, domainNames, now }) {
  if (!sites.length) return null;
  return (
    <details className="al-more">
      <summary>{W.sitesSummary(sites)}</summary>
      <ul className="al-sites">
        {sites.map((s) => (
          <li key={s.node} className={`al-site${s.watched ? "" : " al-unwatched"}`}>
            <div className="al-site-head">
              <strong>{s.node}</strong>
              {s.domain && <span>{domainNames[s.domain] || s.domain}</span>}
            </div>
            <p>{W.siteStatus(s, now)}</p>
            {s.watched && s.lines.length > 0 && (
              <ul className="al-lines">
                {s.lines.map((l) => (
                  <li key={l.name} className={`al-line al-${W.lineTone(l.state)}`}>
                    <span className="al-line-name">{l.label ? `${l.label} (${l.name})` : l.name}</span>
                    <span className="al-line-state">{W.lineState(l.state)}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

function Recent({ recent }) {
  if (!recent.length) return null;
  return (
    <details className="al-more">
      <summary>Recently ({recent.length})</summary>
      <ul className="al-alerts al-recent">
        {recent.map((a) => <AlertItem key={`${a.key}@${a.ended_at}`} alert={a} />)}
      </ul>
    </details>
  );
}

function Lists({ routes, send, reload }) {
  if (!routes?.lists.length) return null;
  const now = Date.now();
  return (
    <section className="panel-card al-panel" aria-labelledby="al-lists-title">
      <div className="al-section-head">
        <h2 id="al-lists-title">Who gets alerts</h2>
        <p>{W.LISTS_DETAIL}</p>
      </div>
      {routes.lists.map((l) => (
        <List key={l.id} list={l} me={routes.me} results={routes.results} now={now} send={send} reload={reload} />
      ))}
    </section>
  );
}

/* One list: the people on it as far as this account may see, and for the
   list's admins its chat webhooks and a test. */
function List({ list, me, results, now, send, reload }) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [hook, setHook] = useState("");
  const people = list.emails.length + list.others;

  const act = async (key, path, method, body, done, timeoutMs) => {
    setBusy(key);
    setMsg(null);
    const r = await send(path, method, body, timeoutMs);
    setBusy("");
    setMsg(r.ok ? done(r.data) : { ok: false, text: r.error });
  };
  const remove = (to) =>
    act(`rm:${to}`, "/alerts/recipients", "DELETE", { route: list.id, to }, () => ({ ok: true, text: W.removed(to) }));
  const test = () =>
    act("test", "/alerts/test", "POST", { route: list.id }, (d) => {
      reload();
      return W.testOutcome(d.results || []);
    }, SEND_MS);
  const addHook = (e) => {
    e.preventDefault();
    act("hook", "/alerts/recipients", "POST", { route: list.id, to: hook }, (d) => {
      setHook("");
      return W.hookAdded(d);
    }, SEND_MS);
  };

  return (
    <div className="al-list">
      <div className="al-list-head">
        <div>
          <h3>{list.name}</h3>
          <p className="al-help">{list.id === "on_call" ? W.ON_CALL_HELP : W.peopleCount(people)}</p>
        </div>
        {list.manage && (
          <button
            type="button" className="al-btn al-small" onClick={test}
            disabled={!!busy || !(list.emails.length + list.webhooks.length)}
          >
            <FiSend aria-hidden="true" /> {busy === "test" ? "Sending…" : "Send a test"}
          </button>
        )}
      </div>
      {!people && !list.webhooks.length && <p className="al-empty">{W.empty(list)}</p>}
      <ul className="al-members">
        {list.emails.map((s) => (
          <Member
            key={s.email} to={s.email} note={W.addedBy(s, me, now)} result={results[s.email]} now={now}
            busy={busy === `rm:${s.email}`}
            onRemove={list.manage || s.added_by === me ? () => remove(s.email) : null}
          />
        ))}
        {list.webhooks.map((h) => (
          <Member
            key={h} to={h} webhook note="Chat app" result={results[h]} now={now}
            busy={busy === `rm:${h}`} onRemove={() => remove(h)}
          />
        ))}
      </ul>
      {list.others > 0 && <p className="al-others">{W.others(list.others)}</p>}
      {list.manage && (
        <details className="al-hook">
          <summary>Also post alerts to a chat app</summary>
          <form className="al-hook-form" onSubmit={addHook}>
            <input
              type="url" required className="al-input" value={hook} onChange={(e) => setHook(e.target.value)}
              placeholder="https://hooks.slack.com/services/…" aria-label={`Chat app webhook for ${list.name}`}
            />
            <button type="submit" className="al-btn al-small" disabled={!!busy || !hook.trim()}>Add</button>
          </form>
          <p className="al-help">{W.WEBHOOK_HELP}</p>
        </details>
      )}
      {msg && <p className={msg.ok ? "al-notice" : "al-error"} role={msg.ok ? "status" : "alert"}>{msg.text}</p>}
    </div>
  );
}

/* A soft colour per address, so a long list is easy to scan. */
const hue = (s) => [...s].reduce((sum, c) => sum + c.charCodeAt(0), 0) % 5;

function Member({ to, webhook, note, result, now, busy, onRemove }) {
  const label = W.recipientLabel(to);
  return (
    <li className="al-member">
      <span className={`al-avatar al-hue-${webhook ? "hook" : hue(to)}`} aria-hidden="true">
        {webhook ? <FiHash /> : label[0].toUpperCase()}
      </span>
      <span className="al-member-text">
        <span className="al-member-name">{label}</span>
        <span className="al-member-note">{note}</span>
        {result && !result.ok && <span className="al-member-fail">{W.lastResult(result, now)}</span>}
      </span>
      {onRemove && (
        <button type="button" className="al-remove" onClick={onRemove} disabled={busy} aria-label={`Remove ${label}`}>
          <FiX aria-hidden="true" /> Remove
        </button>
      )}
    </li>
  );
}
